import { inject, Injectable, makeStateKey, PLATFORM_ID, signal, TransferState } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { defer, map, Observable, of, shareReplay, tap } from 'rxjs';
import { ApiService } from '../../../shared/services/api.service';
import { TokenService } from '../../../shared/services/token.service';
import { resolveHeroImageUrl } from '../../../shared/utils/home-blocks.utils';
import { withTransferState } from '../../../shared/utils/transfer-state.util';
import {
  HeroCarouselData,
  HeroContentAlign,
  HeroImageAsset,
  HeroOverlayStrength,
  HeroSlide,
  HeroTile,
} from '../models/hero.model';

// Shape crudo del payload (`GET /api/hero-carousel`). Difiere de
// `HeroCarouselData` en que:
//  - `id` viaja como número (PK de BD), no string.
//  - `imageUrl` viaja relativo (`images/heroes/<file>`); el componente espera
//    URL absoluta.
// El mapper interno (`adaptSlide`) reconcilia ambos antes de exponer el
// `HeroCarouselData` al `HeroComponent`.
interface ApiHeroCarouselResponse {
  autoplayMs?: number;
  loading?: boolean;
  slides: ApiHeroSlide[];
}

// Campos comunes a todos los slides del API, con `id` como número (PK de BD).
interface ApiHeroSlideBase {
  id: number | string;
  eyebrow?: string;
  headline: string;
  subheadline: string;
  badge?: HeroSlide['badge'];
  primaryAction: HeroSlide['primaryAction'];
  secondaryAction?: HeroSlide['secondaryAction'];
}

type ApiHeroSlide =
  | (ApiHeroSlideBase & { type: 'tiles'; tiles: ApiHeroTile[] })
  | (ApiHeroSlideBase & { type: 'media-right'; media: HeroImageAsset })
  | (ApiHeroSlideBase & { type: 'angled-media'; media: HeroImageAsset })
  | (ApiHeroSlideBase & {
      type: 'background';
      backgroundImage: HeroImageAsset;
      overlayStrength: HeroOverlayStrength;
      contentAlign: HeroContentAlign;
    });

interface ApiHeroTile {
  id: number | string;
  title: string;
  subtitle?: string;
  image: HeroImageAsset;
  route: string;
  queryParams?: HeroTile['queryParams'];
}

// Clave de TransferState para pasar el carrusel hero del render server al
// cliente, evitando el re-fetch tras la hidratación (el "flash" data→skeleton).
const HERO_KEY = makeStateKey<HeroCarouselData>('perron.hero.carousel');

/**
 * Cliente del endpoint `GET /api/hero-carousel`. Auth opcional — el endpoint
 * tolera Bearer de cliente (lo trata como `guests`) o Bearer de usuario (lo
 * trata como `authenticated`) y filtra por audiencia. El `authInterceptor`
 * adjunta el client token automáticamente; el backend lo ignora a nivel de
 * audiencia, así que no hace falta excluir esta ruta del interceptor.
 *
 * Cache de sesión vía `shareReplay(1)`. Backend tiene cache de 5 min por
 * audiencia; el front evita re-fetchear dentro de la misma sesión.
 *
 * Además usa `TransferState` (patrón de `CategoryService`): el server serializa
 * el `HeroCarouselData` ya adaptado y el cliente hidrata con él sin volver a
 * pedir `/hero-carousel`. Como la request lleva `Authorization`, queda fuera del
 * transfer-cache HTTP automático de Angular, así que la propagamos a mano.
 *
 * Excepción por AUDIENCIA: `/hero-carousel` filtra slides por `visible_to`
 * (all/guests/authenticated). En SSR sólo hay client token → el server siempre
 * renderiza la audiencia 'guests'. Si en el cliente hay sesión de usuario,
 * ignoramos el snapshot del server y refetcheamos, para no congelar el hero de
 * invitado a un usuario logueado. Los invitados (mayoría) sí usan el snapshot.
 */
@Injectable({ providedIn: 'root' })
export class HeroService {
  private readonly api = inject(ApiService);
  private readonly tokenService = inject(TokenService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly transferState = inject(TransferState);

  private readonly _cached = signal<HeroCarouselData | null>(null);
  private cachedRequest$?: Observable<HeroCarouselData>;

  /**
   * Carga el contenido del carrusel hero. Devuelve `slides: []` si el backend
   * no tiene ningún slide activo para la audiencia actual — es válido, el
   * `HeroComponent` lo interpreta como "no renderizar carrusel".
   */
  getHero(): Observable<HeroCarouselData> {
    const cached = this._cached();
    if (cached) return of(cached);

    if (!this.cachedRequest$) {
      // Serializamos el `HeroCarouselData` ya adaptado (loading:false, URLs de
      // imagen resueltas con `environment.imagesUrl`, que es constante de build
      // e idéntica en server y cliente).
      // `defer` para que la request sólo se construya/dispare en un MISS; en un
      // HIT del TransferState no llegamos a llamar al API.
      const request$ = defer(() =>
        this.api.get<ApiHeroCarouselResponse>('/hero-carousel').pipe(map((res) => adaptCarousel(res))),
      );

      // Un usuario logueado debe recibir su audiencia; el snapshot del server es
      // siempre 'guests'. Sólo en ese caso saltamos el TransferState.
      const hasUserSession = this.isBrowser && !!this.tokenService.getUserToken();

      this.cachedRequest$ = (
        hasUserSession
          ? request$
          : withTransferState(this.transferState, this.isBrowser, HERO_KEY, request$)
      ).pipe(
        tap((data) => this._cached.set(data)),
        shareReplay(1),
      );
    }
    return this.cachedRequest$;
  }
}

function adaptCarousel(res: ApiHeroCarouselResponse | null | undefined): HeroCarouselData {
  const slides = Array.isArray(res?.slides) ? res!.slides : [];
  return {
    autoplayMs: res?.autoplayMs,
    loading: false,
    slides: slides.map(adaptSlide),
  };
}

function adaptSlide(api: ApiHeroSlide): HeroSlide {
  const base = {
    id: String(api.id),
    type: api.type,
    eyebrow: api.eyebrow,
    headline: api.headline,
    subheadline: api.subheadline,
    badge: api.badge,
    primaryAction: api.primaryAction,
    secondaryAction: api.secondaryAction,
  };

  switch (api.type) {
    case 'tiles':
      return {
        ...base,
        type: 'tiles',
        tiles: (api.tiles ?? []).map(adaptTile),
      };
    case 'media-right':
      return {
        ...base,
        type: 'media-right',
        // Defensa: si el backend omitiera `media` por error, dejamos un asset
        // mínimo para no crashear el render. El bloque saldría sin imagen.
        media: adaptImage(api.media),
      };
    case 'angled-media':
      return {
        ...base,
        type: 'angled-media',
        media: adaptImage(api.media),
      };
    case 'background':
      return {
        ...base,
        type: 'background',
        backgroundImage: adaptImage(api.backgroundImage),
        overlayStrength: api.overlayStrength,
        contentAlign: api.contentAlign,
      };
  }
}

function adaptTile(api: ApiHeroTile): HeroTile {
  return {
    id: String(api.id),
    title: api.title,
    subtitle: api.subtitle,
    image: adaptImage(api.image),
    route: api.route,
    queryParams: api.queryParams,
  };
}

function adaptImage(asset: HeroImageAsset | undefined): HeroImageAsset {
  if (!asset) {
    return { imageUrl: '', alt: '', width: 0, height: 0 };
  }
  return { ...asset, imageUrl: resolveHeroImageUrl(asset.imageUrl) };
}
